import { BufferAttribute, BufferGeometry, Matrix4, Mesh, Vector3 } from 'three'
import type { TileRenderItem } from '@/plugins/tiles/contracts'

/** 按节点/group 顺序枚举有效三角形，同一 Mesh 的重复首索引只访问一次。 */
function forEachTriangle(item: TileRenderItem, visit: (mesh: Mesh, first: number) => void): void {
  item.content.object3D.traverseVisible((object) => {
    if (!(object instanceof Mesh)) return
    const geometry = object.geometry
    const attribute = geometry.getAttribute('position')
    if (!attribute) return
    const count = geometry.index?.count ?? attribute.count
    const start = geometry.drawRange.start
    const end = Math.min(count, start + geometry.drawRange.count)
    const groups = Array.isArray(object.material) ? geometry.groups : [{ start: 0, count }]
    const visited = new Set<number>()
    for (const group of groups) {
      const material = Array.isArray(object.material)
        ? object.material[group.materialIndex ?? 0]
        : object.material
      if (!material?.visible) continue
      for (
        let i = Math.max(start, group.start);
        i + 2 < Math.min(end, group.start + group.count);
        i += 3
      ) {
        if (visited.has(i)) continue
        visited.add(i)
        visit(object, i)
      }
    }
  })
}

/** 同步双遍枚举，输出只分配一次；计数和填充遵循相同分组与可见性规则。 */
export function createCollisionGeometry(item: TileRenderItem): BufferGeometry {
  item.content.object3D.updateMatrixWorld(true)
  let triangles = 0
  forEachTriangle(item, () => triangles++)
  const length = triangles * 9
  if (!Number.isSafeInteger(length)) throw new RangeError('碰撞坐标数量超出安全范围')
  const positions = new Float32Array(length)
  const point = new Vector3()
  const matrix = new Matrix4()
  let previous: Mesh | undefined
  let mirrored = false
  let offset = 0
  forEachTriangle(item, (mesh, first) => {
    if (mesh !== previous) {
      previous = mesh
      matrix.multiplyMatrices(item.matrixWorld, mesh.matrixWorld)
      mirrored = matrix.determinant() < 0
    }
    const attribute = mesh.geometry.getAttribute('position')
    for (let corner = 0; corner < 3; corner++) {
      // 与 Three 的镜像 front-face 翻转保持相同绕序。
      const indexOffset = first + (mirrored && corner > 0 ? 3 - corner : corner)
      const index = mesh.geometry.index?.getX(indexOffset) ?? indexOffset
      point.fromBufferAttribute(attribute, index).applyMatrix4(matrix)
      positions[offset++] = point.x
      positions[offset++] = point.y
      positions[offset++] = point.z
    }
  })
  return new BufferGeometry().setAttribute('position', new BufferAttribute(positions, 3))
}
